import {isUndefined} from 'lodash';
import React, {FC, PropsWithChildren, ReactNode, useRef} from 'react';
import styled, {css} from 'styled-components';

import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {Trans} from '../../../../libs/shared/helpers/src/internationalization/react';
import {ExtractProps, Handler} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon, IconName} from '../../../../libs/shared/ui/src/components/icon/icon';
import {Loader} from '../../../../libs/shared/ui/src/components/loaders/loader';
import {VisuallyHidden} from '../../../../libs/shared/ui/src/components/visuallyHidden/visuallyHidden';
import {VisualSizesEnum} from '../../../../libs/shared/ui/src/styles/commonStyles';
import {fontSizes, fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {ellipsis} from '../../../../libs/shared/ui/src/styles/mixins';
import {makeSizeConstants} from '../../../../libs/shared/ui/src/styles/styleHelpers';
import {usePush} from '../../../../srcCommon/push/pushContext';

/*
 * Props.
 */

export interface AsyncLoaderProps extends ExtractProps<typeof Loader> {
  size?: VisualSizesEnum;
  status: AsyncStatusesEnum;
  error?: ReactNode;
  /** Whether to keep the retry button hidden after we go back online. Defaults to false. */
  shouldHideRetryAfterReconnect?: boolean;
  onRetryClick: Handler;
  color?: string;
}

/*
 * Style.
 */

const retryIcons = makeSizeConstants<IconName>('loaderRetrySmall', 'loaderRetrySmall', 'loaderRetry');
const paddings = makeSizeConstants('10px', '10px', '18px');
const messageFontSizes = makeSizeConstants(fontSizes.small, fontSizes.small, fontSizes.large);
const smallMessageFontSizes = makeSizeConstants(fontSizes.verySmall, fontSizes.verySmall, fontSizes.medium);
const smallMessageMarginTop = makeSizeConstants('4px', '4px', '6px');

const StyledLoaderDiv = styled.div`
  display: flex;
  justify-content: center;
`;

interface SizeStyleProps {
  $size: VisualSizesEnum;
}

interface MessageDivStyleProps extends SizeStyleProps {
  $isBelowRetryButton: boolean;
  $color: string | undefined;
}

const StyledErrorDiv = styled.div<SizeStyleProps>`
  padding: 0 ${(p) => paddings[p.$size]};
  display: grid;

  grid-auto-flow: ${(p) => (p.$size === VisualSizesEnum.SMALL ? 'column' : 'row')};
  grid-template-columns: auto;
  grid-column-gap: 10px;

  align-items: center;
  justify-items: center;
  align-content: center;
  justify-content: center;
`;

const StyledIconButton = styled(ChromelessButton)`
  color: ${(p) => p.theme.greys.shade50};

  &:hover {
    color: ${(p) => p.theme.greys.shade60};
  }
`;

const StyledMessageDiv = styled.div<MessageDivStyleProps>`
  max-width: 100%;

  color: ${(p) => p.$color || p.theme.greys.shade60};
  font-size: ${(p) => messageFontSizes[p.$size]};
  font-weight: ${fontWeights.semibold};
  ${(p) => addMessageDivMarginStyles(p)};
  ${ellipsis()};
`;

function addMessageDivMarginStyles(p: MessageDivStyleProps) {
  if (!p.$isBelowRetryButton || p.$size === VisualSizesEnum.SMALL) {
    return css`
      margin-top: 0px;
    `;
  }

  return css`
    margin-top: ${paddings[p.$size]};
  `;
}

const StyledSmallMessageDiv = styled.div<SizeStyleProps>`
  max-width: 100%;

  color: ${(p) => p.theme.greys.shade60};
  font-size: ${(p) => smallMessageFontSizes[p.$size]};
  font-weight: ${fontWeights.normal};
  margin-top: ${(p) => smallMessageMarginTop[p.$size]};
  ${ellipsis()};
`;

/*
 * Component.
 */

export const AsyncLoader: FC<PropsWithChildren<AsyncLoaderProps>> = (props) => {
  const {status} = props;
  const {isOffline, wasOffline} = useOffline(status);

  // Loading state.
  if (status === AsyncStatusesEnum.LOADING) {
    return renderLoader(props);
  }

  // Error state.
  if (status === AsyncStatusesEnum.FAILED) {
    return renderError(props, isOffline, wasOffline);
  }

  // Loaded state.
  return <>{props.children}</>;
};

function renderLoader(props: AsyncLoaderProps) {
  return (
    <StyledLoaderDiv>
      <Loader {...props} />
    </StyledLoaderDiv>
  );
}

function renderError(props: AsyncLoaderProps, isOffline: boolean, wasOffline: boolean) {
  const {size = VisualSizesEnum.LARGE, onRetryClick, color} = props;

  // If no error prop is present, use a default error message.
  const error = isUndefined(props.error) ? renderDefaultError(wasOffline) : props.error;

  // Hide the retry button if we're offline (or if we were previously offline and the retry is automatic).
  const shouldHideRetry = props.shouldHideRetryAfterReconnect ? wasOffline : isOffline;
  const shouldShowRetry = !shouldHideRetry;

  return (
    <StyledErrorDiv
      onClick={onRetryClick}
      $size={size}
      {...buildInteractionIdProps('loaderErrorRetryButton')}
    >
      {shouldShowRetry && (
        <StyledIconButton>
          <Icon name={retryIcons[size]} />
          <VisuallyHidden>
            <Trans id="asyncloader-retry">Retry</Trans>
          </VisuallyHidden>
        </StyledIconButton>
      )}
      {error && (
        <StyledMessageDiv $size={size} $isBelowRetryButton={shouldShowRetry} $color={color}>
          {error}
        </StyledMessageDiv>
      )}
      {shouldShowRetry && size !== VisualSizesEnum.SMALL && (
        <StyledSmallMessageDiv $size={size}>
          <Trans id="asyncloader-retrytext">Please click to retry</Trans>
        </StyledSmallMessageDiv>
      )}
    </StyledErrorDiv>
  );
}

function renderDefaultError(wasOffline: boolean) {
  return wasOffline ? (
    <Trans id="asyncloader-offline">Not available offline</Trans>
  ) : (
    <Trans id="asyncloader-error">An error occurred</Trans>
  );
}

/*
 * Hooks.
 */

function useOffline(status: AsyncStatusesEnum): {isOffline: boolean; wasOffline: boolean} {
  const {isConnected} = usePush();
  const wasOfflineRef = useRef(false);

  const isOffline = !isConnected;

  // Persist the offline state until we transition away from the failed status.
  if (isOffline) {
    wasOfflineRef.current = true;
  } else if (status !== AsyncStatusesEnum.FAILED) {
    wasOfflineRef.current = false;
  }

  return {isOffline, wasOffline: wasOfflineRef.current};
}
